<template>
  <section class="container content">
    <div>
      <h1 class="mt-3">
        {{ $t('Registrieren') }}
      </h1>

      <div class="mb-4">
        {{ $t('Hast du bereits einen Account?') }}
        <nuxt-link :to="localePath(`/login${nextQuery}`)">
          {{ $t('Jetzt anmelden') }} »
        </nuxt-link>
      </div>

      <b-form @submit.prevent="onSubmit">
        <b-form-group
          id="input-group-1"
          :label="$t('Benutzername') + ':'"
          label-for="input-1"
          class="form-group--required"
        >
          <b-form-input
            id="input-1"
            v-model="credentials.username"
            type="text"
            required
            :placeholder="$t('Benutzername')"
          />
        </b-form-group>

        <b-form-group
          id="input-group-2"
          :label="$t('Passwort') + ':'"
          label-for="input-password"
          class="form-group--required"
        >
          <div
            id="input-password"
            class="d-flex"
          >
            <div
              class="showPasswordButton"
              @click.prevent="toggleShow"
            >
              <Eye v-if="showPassword" />
              <EyeOff v-else />
            </div>

            <b-form-input
              v-if="showPassword"
              id="input-password-text"
              v-model="credentials.password"
              type="text"
              required
              :placeholder="$t('Passwort')"
              autocomplete="new-password"
            />
            <b-form-input
              v-else
              id="input-password-password"
              v-model="credentials.password"
              type="password"
              required
              :placeholder="$t('Passwort')"
              autocomplete="new-password"
            />
          </div>
        </b-form-group>

        <b-form-group
          id="input-group-3"
          :label="$t('Name') + ':'"
          label-for="input-3"
          class="form-group--required"
        >
          <b-form-input
            id="input-3"
            v-model="credentials.name"
            type="text"
            :placeholder="$t('Name')"
            required
          />
        </b-form-group>

        <b-form-group
          id="input-group-4"
          :label="$t('E-Mail') + ':'"
          label-for="input-4"
          :description="
            $t(
              'Eine E-Mail muss nicht zwingend angegeben werden. Doch falls du dein Passwort vergisst, kannst du so ein neues erhalten. Wenn du über Neuerungen informiert werden möchtest, musst du eine E-Mail angeben.'
            )
          "
        >
          <b-form-input
            id="input-4"
            v-model="credentials.email"
            type="email"
            :required="credentials.newsletter"
            :placeholder="$t('E-Mailadresse')"
          />
        </b-form-group>

        <div
          v-if="error"
          class="invalid-feedback d-block my-4"
        >
          <i18n path="registration_to_login">
            <template #link_to_login>
              <nuxt-link :to="localePath(`/login${nextQuery}`)">
                {{ $t('melde dich an') }}
              </nuxt-link>
              .
            </template>
          </i18n>
        </div>

        <b-form-group>
          <b-form-checkbox
            v-model="credentials.newsletter"
            name="newsletter"
            switch
          >
            {{
              $t(
                'Ich möchte über Neuigkeiten von Lernpfad.ch informiert werden.'
              )
            }}
          </b-form-checkbox>
        </b-form-group>

        <b-form-group>
          <b-form-checkbox
            id="TocAccept"
            v-model="acceptToc"
            name="acceptToc"
            switch
          >
            <i18n path="registration_term_of_use">
              <template #link_to_tou>
                <nuxt-link
                  :to="localePath('/agb')"
                  target="_blank"
                >
                  {{ $t('Nutzungsbedingungen') }}
                </nuxt-link>
              </template>
            </i18n>

            <div
              v-if="acceptTocError && !acceptToc"
              class="invalid-feedback d-block my-0"
            >
              {{ $t('Du musst die Nutzungsbedingungen akzeptieren') }}
            </div>
          </b-form-checkbox>
        </b-form-group>
      </b-form>
    </div>
  </section>
</template>

<script>
import LearnPathUtils from '../mixins/LearnPathUtils'
import { ampli } from '../mixins/Amplitude/index.js'
import EyeOff from '../assets/images/eyeOff.svg'
import Eye from '../assets/images/eye.svg'
import Flow from '../mixins/Flow'
import registerGql from './registrieren.gql'

export default {
  components: {
    EyeOff,
    Eye,
  },
  mixins: [LearnPathUtils, Flow, ampli],

  data() {
    return {
      processing: false,
      error: false,
      acceptToc: false,
      acceptTocError: false,
      credentials: {
        username: '',
        email: '',
        password: '',
        name: '',
        newsletter: false,
        language: this.$i18n.locale,
      },
      showPassword: false,
    }
  },

  head() {
    return {
      title: this.$i18n.t('Registrieren | Lernpfad'),
    }
  },

  methods: {
    toggleShow() {
      this.showPassword = !this.showPassword
    },
    async onSubmit() {
      if (!this.acceptToc) {
        this.acceptTocError = true
        return
      }

      this.processing = true
      this.error = false

      try {
        // FIXME: seems to be a job for the backend ...
        this.credentials.username = this.credentials.username
          .trim()
          .toLowerCase()
        this.credentials.email = this.credentials.email.trim().toLowerCase()

        const res = await this.$apollo.mutate({
          mutation: registerGql,
          variables: this.credentials,
        })

        await this.$apolloHelpers.onLogin(res.data.createUser.tokenAuth.token)

        ampli.identify(this.$store.state.user.id, {
          email: this.$store.state.user.email
            ? this.$store.state.user.email
            : '',
          username: this.credentials.username,
          name: this.$store.state.user.name,
        })
        ampli.register({
          username: this.credentials.username,
        })
      } catch (e) {
        this.error = true
      }

      // TODO: identical with login, refactor with a mixin, ev. Flow

      if (!this.error) {
        let pathRef
        if (this.$store.state.tmpLearnPath) {
          pathRef = await this.saveLearnPath(this.$store.state.tmpLearnPath)
        } else if (this.$store.state.copyLearnPath) {
          pathRef = await this.copyLearnPath(this.$store.state.copyLearnPath)
        }

        if (pathRef) {
          this.$router.push(this.localePath({ path: `/pfad/${pathRef}/admin` }))
        } else {
          this.$router.push(
            this.localePath({
              path: this.$route.query.next ? this.$route.query.next : '/',
            })
          )
        }
      }

      this.processing = false
    },
  },
}
</script>

<style scoped lang="scss">
.showPasswordButton {
  margin: 0 1rem 0 0;
}
</style>
